import React, { useState, useRef, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    Box,
    Stack,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    Divider,
    MenuItem,
    Collapse,
    CircularProgress,
} from "@mui/material";
import html2pdf from 'html2pdf.js'
import Grid from "@mui/material/Grid";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import "react-datepicker/dist/react-datepicker.css";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { ApiGetEntityTypeList, ApiGetClientEntityListById, ApiGetVenueList, ApiGetClientEntityComparisionsByEntityType, ApiGetClientEntityComparisionsByEntityTypeExcel } from "../../../apiService/Core/Apicall.tsx";
import { toast } from "react-toastify";

const PositionRankingReport: React.FC = () => {
    const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('year'));
    const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
    const [open, setOpen] = useState(false);
    const [selectedVenues, setSelectedVenues] = useState<string[]>([]);
    const [filter, setFilter] = useState('--Select Entity--');
    const [entityTypes, setEntityTypes] = useState<any[]>([]);
    const [venueList, setVenueList] = useState<Record<string, { id: string, name: string }[]>>({});
    const [loading, setLoading] = useState(false);
    const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
    const [expandedEntities, setExpandedEntities] = useState<string[]>([]);
    const [error, setError] = useState(null);
    const [clientEntities, setClientEntities] = useState<any[]>([]);
    const [filteredEntities, setFilteredEntities] = useState<any[]>([]);
    const [reportData, setReportData] = useState<any[]>([]);
    const groupRankingRef = useRef<HTMLDivElement>(null);
    const venueRankingRef = useRef<HTMLDivElement>(null);
    const groupCategoryRef = useRef<HTMLDivElement>(null);
    const venueCategoryRef = useRef<HTMLDivElement>(null);
    const pdfRef = useRef<HTMLDivElement>(null);
    const scrollToSection = (ref: React.RefObject<HTMLDivElement | null>) => {
        if (ref.current) {
            ref.current.scrollIntoView({ behavior: "smooth" });
        }
    };
    const getDenseRankings = (data: { name: string, rating: number }[]) => {
        const sorted = [...data].sort((a, b) => b.rating - a.rating);

        let rank = 1;
        return sorted.map((item, index) => {
            if (index > 0 && item.rating !== sorted[index - 1].rating) {
                rank++;
            }
            return { ...item, rank };
        });
    };

    const getOrdinalSuffix = (i: number) => {
        const j = i % 10;
        const k = i % 100;
        if (j === 1 && k !== 11) return `${i}st`;
        if (j === 2 && k !== 12) return `${i}nd`;
        if (j === 3 && k !== 13) return `${i}rd`;
        return `${i}th`;
    };

    const groupRanked = getDenseRankings(
        reportData.map(item => ({
            name: item.entityName,
            rating: item.averageRating
        }))
    );

    const venueRanked = getDenseRankings(
        reportData.flatMap(entity =>
            (entity.venues || []).map(venue => ({
                name: venue.venueName,
                rating: venue.averageRating
            }))
        )
    );
    const categoryNames = Array.from(new Set(
        reportData.flatMap(group =>
            group.venues.flatMap(venue =>
                venue.categories.map(cat => cat.categoryName)
            )
        )
    ));
    const getCategoryRankings = (source: 'group' | 'venue') => {
        return categoryNames.map(category => {
            const entries = reportData.flatMap(group =>
                group.venues.flatMap(venue =>
                    venue.categories
                        .filter(cat => cat.categoryName === category)
                        .map(cat => ({
                            name: source === 'group'
                                ? `${group.entityName}`
                                : `${venue.venueName} (${group.entityName})`, // <- updated
                            rating: cat.averageRating
                        }))
                )
            );

            const ranked = getDenseRankings(entries);
            return { category, ranked };
        });
    };

    const getGroupCategoryRankings = () => {
        const categoryMap: Record<string, { name: string; rating: number }[]> = {};

        reportData.forEach(group => {
            const entityName = group.entityName;
            const categorySums: Record<string, { total: number; count: number }> = {};

            group.venues.forEach(venue => {
                venue.categories.forEach(category => {
                    const catName = category.categoryName;
                    if (!categorySums[catName]) {
                        categorySums[catName] = { total: 0, count: 0 };
                    }
                    categorySums[catName].total += category.averageRating;
                    categorySums[catName].count += 1;
                });
            });

            Object.entries(categorySums).forEach(([catName, stats]) => {
                if (!categoryMap[catName]) {
                    categoryMap[catName] = [];
                }
                const avg = parseFloat((stats.total / stats.count).toFixed(2));
                categoryMap[catName].push({ name: entityName, rating: avg });
            });
        });

        return Object.entries(categoryMap).map(([category, entries]) => ({
            category,
            ranked: getDenseRankings(entries)
        }));
    };

    const groupCategoryRankings = getGroupCategoryRankings();


    // const groupCategoryRankings = getCategoryRankings('group');
    const venueCategoryRankings = getCategoryRankings('venue');

    const toggleEntity = async (entityId: string) => {
        if (selectedEntities.includes(entityId)) {
            setSelectedEntities(prev => prev.filter(id => id !== entityId));
            setSelectedVenues(prev => prev.filter(venue => !venue.startsWith(`${entityId}-`)));
        } else {
            setSelectedEntities(prev => [...prev, entityId]);

            if (venueList[entityId]) {
                const entityVenues = venueList[entityId].map(venue => `${entityId}-${venue.id}`);
                setSelectedVenues(prev => [...new Set([...prev, ...entityVenues])]);
            } else {
                const venues = await fetchVenuesForEntity(entityId);
                const entityVenues = venues.map(venue => `${entityId}-${venue.id}`);
                setSelectedVenues(prev => [...new Set([...prev, ...entityVenues])]);
            }
        }
    };

    const toggleVenue = (entityId: string, venueId: string) => {
        const venueKey = `${entityId}-${venueId}`;
        setSelectedVenues(prev =>
            prev.includes(venueKey)
                ? prev.filter(v => v !== venueKey)
                : [...prev, venueKey]
        );

        const allVenuesSelected = venueList[entityId]?.every(v =>
            selectedVenues.includes(`${entityId}-${v.id}`) || v.id === venueId
        );

        if (allVenuesSelected && !selectedEntities.includes(entityId)) {
            setSelectedEntities(prev => [...prev, entityId]);
        } else if (!allVenuesSelected && selectedEntities.includes(entityId)) {
            setSelectedEntities(prev => prev.filter(id => id !== entityId));
        }
    };


    const toggleEntityExpand = (entityId: string) => {
        if (!expandedEntities.includes(entityId)) {
            fetchVenuesForEntity(entityId);
        }
        setExpandedEntities(prev =>
            prev.includes(entityId)
                ? prev.filter(id => id !== entityId)
                : [...prev, entityId]
        );
    };
    useEffect(() => {
        const fetchEntityTypes = async () => {
            try {
                const response: any = await ApiGetEntityTypeList();
                console.log("Entity types response", response.data);
                const transformedData = response.data.map((item: any) => ({
                    value: item.entityTypeName,
                    label: item.entityTypeName,
                }));

                setEntityTypes(transformedData);
            } catch (err) {
                console.error("Error fetching entity types:", err);
                setError(err);
            }
        };

        fetchEntityTypes();
    }, []);
    useEffect(() => {
        const fetchClientEntities = async () => {
            try {
                const queryString = `?offset=0&limit=100&clientEntityStatus=active`;
                const response: any = await ApiGetClientEntityListById(queryString);

                setClientEntities(response.data);
                setFilteredEntities(response.data);
            } catch (err) {
                console.error("Error fetching client entities:", err);
            }
        };

        fetchClientEntities();
    }, []);
    const handleFilterChange = (e: React.ChangeEvent<{ value: unknown }>) => {
        const value = e.target.value as string;
        setFilter(value);

        if (value === "--Select Entity--") {
            setFilteredEntities(clientEntities);
        } else {
            const filtered = clientEntities.filter(
                entity => entity.entityTypeId?.entityTypeName === value
            );
            setFilteredEntities(filtered);
        }
    };
    const fetchVenuesForEntity = async (entityId: string) => {
        try {
            const queryString = `?clientEntityId=${entityId}&venueStatus=active`;
            const response: any = await ApiGetVenueList(queryString);
            const venues = Array.isArray(response.data)
                ? response.data.map((venue: any) => ({
                    id: venue.venueId?.toString() || Math.random().toString(),
                    name: venue.venueName || 'Unnamed Venue'
                }))
                : [];

            setVenueList(prev => ({
                ...prev,
                [entityId]: venues
            }));

            return venues;
        } catch (err) {
            console.error(`Error fetching venues for entity ${entityId}:`, err);
            setVenueList(prev => ({
                ...prev,
                [entityId]: []
            }));
            return [];
        }
    };
    const generateReport = async () => {
        if (!startDate || !endDate) {
            alert("Please select both start and end dates");
            return;
        }

        if (selectedEntities.length === 0 && selectedVenues.length === 0) {
            alert("Please select at least one entity or venue");
            return;
        }

        try {
            setLoading(true);
            const clients = selectedEntities.map(entityId => {
                const entityVenues = selectedVenues
                    .filter(venueKey => venueKey.startsWith(`${entityId}-`))
                    .map(venueKey => venueKey.split('-')[1]);

                return {
                    clientEntityId: parseInt(entityId),
                    venueIds: entityVenues.map(id => parseInt(id))
                };
            });

            const requestBody = {
                clients,
                startMonth: startDate.month() + 1,
                startYear: startDate.year(),
                endMonth: endDate.month() + 1,
                endYear: endDate.year()
            };

            console.log("Sending requestBody:", requestBody);

            const response = await ApiGetClientEntityComparisionsByEntityType(requestBody);
            setReportData(response.data);
            console.log("API response:", response.data);
        } catch (error) {
            console.error("Error generating report:", error);
            setError(error);
        } finally {
            setLoading(false);
        }
    };

    // const handleDownloadPdf = () => {
    //     if (pdfRef.current) {
    //         const opt = {
    //             margin: 0.2,
    //             filename: 'Rating-Ranking-Report.pdf',
    //             image: { type: 'jpeg', quality: 0.98, compression: 'NONE' },
    //             html2canvas: {
    //                 scale: 2,
    //                 useCORS: true,
    //                 letterRendering: true,
    //                 allowTaint: true,
    //                 logging: true,
    //                 backgroundColor: '#FFFFFF',
    //                 ignoreElements: (element) => {
    //                     return element.classList.contains('no-export');
    //                 }

    //             },
    //             jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait', compress: false },
    //             pagebreak: {
    //                 mode: ['avoid-all', 'css', 'legacy'],
    //                 before: '.page-break-before',
    //                 after: '.page-break-after',
    //                 avoid: '.avoid-break'
    //             }
    //         };

    //         html2pdf()
    //             .set(opt)
    //             .from(pdfRef.current)
    //             .outputPdf('blob')
    //             .then((pdfBlob: Blob) => {
    //                 const blobUrl = URL.createObjectURL(pdfBlob);
    //                 const link = document.createElement("a");
    //                 link.href = blobUrl;
    //                 link.download = "Rating-Ranking-Report.pdf";
    //                 document.body.appendChild(link);
    //                 link.click();
    //                 document.body.removeChild(link);
    //                 URL.revokeObjectURL(blobUrl);
    //             })
    //             .catch((err: any) => {
    //                 console.error('PDF generation error:', err);
    //                 toast.error('Failed to generate PDF');
    //             });
    //     }
    // };
    const handleDownloadAsXls = async () => {
         const clients = selectedEntities.map(entityId => {
                const entityVenues = selectedVenues
                    .filter(venueKey => venueKey.startsWith(`${entityId}-`))
                    .map(venueKey => venueKey.split('-')[1]);

                return {
                    clientEntityId: parseInt(entityId),
                    venueIds: entityVenues.map(id => parseInt(id))
                };
            });
            try {
                const requestBody = {
                clients,
                startMonth: startDate.month() + 1,
                startYear: startDate.year(),
                endMonth: endDate.month() + 1,
                endYear: endDate.year()
            };
                const res = await ApiGetClientEntityComparisionsByEntityTypeExcel(requestBody);
                console.log("Trend Data Response", res)
    
                const blob = new Blob([res], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                const url = URL.createObjectURL(blob);
    
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `RatingRanking.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                alert(error)
            }
        }
    return (
        <Box className="manage-admin-container">
            <Box sx={{ margin: 2, justifyContent: 'space-between', alignItems: 'center', display: 'flex', width: '100%', padding: '10px' }}>
                <Box>
                    <Stack direction="row" spacing={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                views={['year', 'month']}
                                label="Start Month"
                                openTo="month"
                                value={startDate}
                                onChange={(newValue) => {
                                    setStartDate(newValue);
                                    if (!newValue) {
                                        setEndDate(null);
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            width: 180,
                                            '& .MuiInputBase-root': { height: 40 }
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                views={['year', 'month']}
                                label="End Month"
                                openTo="month"
                                value={endDate}
                                onChange={(newValue) => setEndDate(newValue)}
                                minDate={startDate || undefined}
                                disabled={!startDate}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            width: 180,
                                            '& .MuiInputBase-root': { height: 40 }
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Stack>
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '40px' }}>Rating  Ranking Report</Typography>
                </Box>
                <Box>
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" sx={{ backgroundColor: "#75724C", borderRadius: "8px", px: 3, "&:hover": { backgroundColor: "#5a522c" } }}
                            onClick={() => handleDownloadAsXls()}>
                            DOWNLOAD AS XLSX
                        </Button>
                        {/* <Button variant="contained" sx={{ backgroundColor: "#75724C", borderRadius: "8px", px: 3, "&:hover": { backgroundColor: "#5a522c" } }}
                            onClick={() => handleDownloadPdf()}>
                            DOWNLOAD AS PDF
                        </Button> */}
                    </Stack>
                </Box>
            </Box>
            <Box sx={{
                margin: 2,
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                width: '100%',
                padding: '10px',
                backgroundColor: "#75724C"
            }}

            >
                <Button sx={{ color: 'white' }} onClick={() => setOpen(true)}>
                    Select Client / Venues / Stores
                </Button>
                <Dialog open={open} maxWidth="sm" fullWidth>
                    <DialogTitle
                        sx={{
                            m: 0,
                            p: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Typography variant="h6">Select Group / Venues / Stores</Typography>
                        <IconButton
                            aria-label="close"
                            onClick={() => {
                                console.log("Close clicked");
                                setOpen(false);
                            }}
                            sx={{
                                color: (theme) => theme.palette.grey[500]
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <TextField
                            select
                            value={filter}
                            onChange={handleFilterChange}
                            fullWidth
                            size="small"
                            sx={{ mb: 2 }}
                        >
                            <MenuItem value="--Select Entity--">--Show All--</MenuItem>
                            {entityTypes.map((entity) => (
                                <MenuItem key={entity.value} value={entity.label}>
                                    {entity.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        {filteredEntities.map((entity) => (
                            <Box key={entity.clientEntityId} sx={{ mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox
                                        checked={selectedEntities.includes(entity.clientEntityId)}
                                        onChange={() => toggleEntity(entity.clientEntityId)}
                                        indeterminate={
                                            venueList[entity.clientEntityId]?.some(venue =>
                                                selectedVenues.includes(`${entity.clientEntityId}-${venue.id}`)
                                                && !venueList[entity.clientEntityId]?.every(venue =>
                                                    selectedVenues.includes(`${entity.clientEntityId}-${venue.id}`))
                                            )}
                                    />
                                    <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                        {entity.entityName}
                                    </Typography>
                                    <IconButton
                                        onClick={() => toggleEntityExpand(entity.clientEntityId)}
                                    >
                                        {expandedEntities.includes(entity.clientEntityId) ? (
                                            <ExpandLessIcon />
                                        ) : (
                                            <ExpandMoreIcon />
                                        )}
                                    </IconButton>
                                </Box>
                                <Collapse in={expandedEntities.includes(entity.clientEntityId)}>
                                    {venueList[entity.clientEntityId] ? (
                                        venueList[entity.clientEntityId].length > 0 ? (
                                            <Grid container spacing={2} sx={{ pl: 4 }}>
                                                {venueList[entity.clientEntityId].map((venue) => (
                                                    <Grid item xs={6} sm={4} md={3} key={venue.id}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={selectedVenues.includes(`${entity.clientEntityId}-${venue.id}`)}
                                                                    onChange={() => toggleVenue(entity.clientEntityId, venue.id)}
                                                                />
                                                            }
                                                            label={venue.name}
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        ) : (
                                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                                <Typography>No venues available</Typography>
                                            </Box>
                                        )
                                    ) : (
                                        <Box sx={{ p: 2, textAlign: 'center' }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    )}
                                </Collapse>
                            </Box>
                        ))}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpen(false)} variant="contained" sx={{ backgroundColor: "#75724C" }}>
                            Save changes
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
            <Box>
                <Button
                    onClick={generateReport}
                    variant="contained"
                    sx={{ backgroundColor: "#75724C" }}
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Generate Report"}
                </Button>
            </Box>
            <Divider sx={{ border: '1px solid gray', width: "100%", mt: '20px' }}></Divider>
            <Box sx={{ margin: 2, justifyContent: 'space-between', alignItems: 'center', display: 'flex', width: '100%', padding: '10px' }}>
                <Box>
                    <Button variant="contained" sx={{ backgroundColor: "#75724C" }} onClick={() => scrollToSection(groupRankingRef)}>
                        Ranking Report - Group
                    </Button>
                </Box>
                <Box>
                    <Button variant="contained" sx={{ backgroundColor: "#75724C" }} onClick={() => scrollToSection(venueRankingRef)}>
                        Ranking Report - SITE/ STORE
                    </Button>
                </Box>
                <Box>
                    <Button variant="contained" sx={{ backgroundColor: "#75724C" }} onClick={() => scrollToSection(groupCategoryRef)}>
                        Category Report - Group
                    </Button>
                </Box>
                <Box>
                    <Button variant="contained" sx={{ backgroundColor: "#75724C" }} onClick={() => scrollToSection(venueCategoryRef)}>
                        Category Report - SITE/ STORE
                    </Button>
                </Box>
            </Box>
            <Divider sx={{ border: '1px solid gray', width: "100%", mt: '10px' }}></Divider>
        <Box ref={pdfRef} sx={{ padding: '10px' }}>
                <Box ref={groupRankingRef}
                    sx={{
                        width: "100%",
                        borderRadius: "8px",
                        border: "2px solid #6b773c",
                        overflow: "hidden",
                        mt: 2,
                        backgroundColor: "#d6e4bc",
                        padding: "20px",
                        pageBreakAfter: 'avoid'
                    }}
                >
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        color="black"
                        textAlign="center"
                        sx={{ padding: "15px", borderBottom: "2px solid white" }}
                    >
                        Group Comparison Report
                    </Typography>

                    <Box
                        sx={{
                            border: "2px solid #6b773c",
                            borderRadius: "8px",
                            backgroundColor: "white",
                            mt: 2,
                            overflow: "hidden",
                        }}
                    >
                        <Table sx={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
                            <TableHead>
                                <TableRow className="table-row-background">
                                    <TableCell sx={{ width: "20%", color: "#fff", fontWeight: "bold", textAlign: "center" }}>
                                        Rank
                                    </TableCell>

                                    <TableCell
                                        sx={{ color: "#fff", fontWeight: "bold", textAlign: "center" }}
                                    >
                                        Group Name
                                    </TableCell>

                                    <TableCell sx={{ color: "#fff", fontWeight: "bold", textAlign: "center" }}>
                                        Rating
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groupRanked.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
                                            {getOrdinalSuffix(item.rank)}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: "center", fontWeight: 'bold' }}>
                                            {item.name}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
                                            {item.rating}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>
                <Box ref={venueRankingRef}
                    sx={{
                        width: "100%",
                        borderRadius: "8px",
                        border: "2px solid #6b773c",
                        overflow: "hidden",
                        mt: 2,
                        backgroundColor: "#d6e4bc",
                        padding: "20px",
                        pageBreakAfter: 'avoid'
                    }}
                >
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        color="black"
                        textAlign="center"
                        sx={{ padding: "15px", borderBottom: "2px solid white" }}
                    >
                        Site Comparison Report
                    </Typography>

                    <Box
                        sx={{
                            border: "2px solid #6b773c",
                            borderRadius: "8px",
                            backgroundColor: "white",
                            mt: 2,
                            overflow: "hidden",
                        }}
                    >
                        <Table sx={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
                            <TableHead>
                                <TableRow className="table-row-background">
                                    <TableCell sx={{ width: "20%", color: "#fff", fontWeight: "bold", textAlign: "center" }}>
                                        Rank
                                    </TableCell>

                                    <TableCell
                                        sx={{ color: "#fff", fontWeight: "bold", textAlign: "center" }}
                                    >
                                        Site / Store Name
                                    </TableCell>

                                    <TableCell sx={{ color: "#fff", fontWeight: "bold", textAlign: "center" }}>
                                        Rating
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {venueRanked.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
                                            {getOrdinalSuffix(item.rank)}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: "center", fontWeight: 'bold' }}>
                                            {item.name}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
                                            {item.rating}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>
                <Box ref={groupCategoryRef}
                    sx={{
                        width: "100%",
                        borderRadius: "8px",
                        border: "2px solid #6b773c",
                        overflow: "hidden",
                        mt: 2,
                        backgroundColor: "#d6e4bc",
                        padding: "20px",
                        pageBreakAfter: 'avoid'
                    }}
                >
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        color="black"
                        textAlign="center"
                        sx={{ padding: "15px", borderBottom: "2px solid white" }}
                    >
                        Category Report (Group Ranking)
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 2,
                            mt: 2,
                            alignItems: "start",
                        }}
                    >
                        {groupCategoryRankings.map(({ category, ranked }) => (
                            <Box
                                key={category}
                                sx={{
                                    border: "2px solid #6b773c",
                                    borderRadius: "8px",
                                    backgroundColor: "white",
                                    overflow: "hidden",
                                    mb: 2,
                                }}
                            >
                                <Typography
                                    fontWeight="bold"
                                    color="white"
                                    className="table-row-background"
                                    sx={{
                                        padding: "15px",
                                        textAlign: "center",
                                        fontSize: '15px'
                                    }}
                                >
                                    {category}
                                </Typography>

                                <Table sx={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
                                    <colgroup>
                                        <col className="col-md-3" />
                                        <col />
                                        <col className="col-md-3" />
                                    </colgroup>
                                    <TableBody>
                                        {ranked.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{
                                                    textAlign: "left",
                                                    padding: "10px",
                                                    fontWeight: "bold",
                                                    borderRight: "2px solid #4d5a2c",
                                                    color: "black",
                                                }}>
                                                    {getOrdinalSuffix(item.rank)}
                                                </TableCell>
                                                <TableCell sx={{
                                                    textAlign: "start",
                                                    padding: "10px",
                                                    fontWeight: "bold",
                                                    color: "black",
                                                    borderRight: "2px solid #4d5a2c",
                                                }}>
                                                    {item.name}
                                                </TableCell>
                                                <TableCell sx={{
                                                    textAlign: "center",
                                                    padding: "10px",
                                                    fontWeight: "bold",
                                                    color: "black",
                                                }}>
                                                    {item.rating}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        ))}

                    </Box>
                </Box>
                <Box ref={venueCategoryRef}
                    sx={{
                        width: "100%",
                        borderRadius: "8px",
                        border: "2px solid #6b773c",
                        overflow: "hidden",
                        mt: 2,
                        backgroundColor: "#d6e4bc",
                        padding: "20px",
                        pageBreakAfter: 'avoid'
                    }}
                >
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        color="black"
                        textAlign="center"
                        sx={{ padding: "15px", borderBottom: "2px solid white" }}
                    >
                        Category Report (Site Ranking)
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 2,
                            mt: 2,
                            alignItems: "start",
                        }}
                    >
                        {venueCategoryRankings.map(({ category, ranked }) => (
                            <Box
                                key={category}
                                sx={{
                                    border: "2px solid #6b773c",
                                    borderRadius: "8px",
                                    backgroundColor: "white",
                                    overflow: "hidden",
                                    mb: 2,
                                }}
                            >
                                <Typography
                                    fontWeight="bold"
                                    color="white"
                                    className="table-row-background"
                                    sx={{
                                        padding: "15px",
                                        textAlign: "center",
                                        fontSize: '15px'
                                    }}
                                >
                                    {category}
                                </Typography>

                                <Table sx={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
                                    <colgroup>
                                        <col className="col-md-3" />
                                        <col />
                                        <col className="col-md-3" />
                                    </colgroup>
                                    <TableBody>
                                        {ranked.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{
                                                    textAlign: "left",
                                                    padding: "10px",
                                                    fontWeight: "bold",
                                                    borderRight: "2px solid #4d5a2c",
                                                    color: "black",
                                                }}>
                                                    {getOrdinalSuffix(item.rank)}
                                                </TableCell>
                                                <TableCell sx={{
                                                    textAlign: "start",
                                                    padding: "10px",
                                                    fontWeight: "bold",
                                                    color: "black",
                                                    borderRight: "2px solid #4d5a2c",
                                                }}>
                                                    {item.name}
                                                </TableCell>
                                                <TableCell sx={{
                                                    textAlign: "center",
                                                    padding: "10px",
                                                    fontWeight: "bold",
                                                    color: "black",
                                                }}>
                                                    {item.rating}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        ))}

                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default PositionRankingReport;